// Admin-side TanStack Query hooks. Uses the authenticated Supabase client;
// RLS rejects writes from users without the right role. See 0004_admin_rls.sql.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { supabase } from './supabase';

/* ---------------------------------------------------------------- site_settings */
export function useAdminSiteSettings() {
  return useQuery({
    queryKey: ['admin', 'site-settings'],
    queryFn: async () => {
      const { data, error } = await supabase.from('site_settings').select('*').eq('id', 1).maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function useUpdateSiteSettings(): UseMutationResult<unknown, Error, Record<string, unknown>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const { data: result, error } = await supabase
        .from('site_settings')
        .upsert({ id: 1, data, updated_by: (await supabase.auth.getUser()).data.user?.id })
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'site-settings'] }),
  });
}

/* ---------------------------------------------------------------- home_sections */
export function useAdminHomeSections() {
  return useQuery({
    queryKey: ['admin', 'home-sections'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('home_sections')
        .select('*')
        .order('display_order');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useUpdateHomeSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, data, is_enabled, display_order }: { key: string; data: Record<string, unknown>; is_enabled?: boolean; display_order?: number }) => {
      const update: Record<string, unknown> = { data };
      if (typeof is_enabled === 'boolean') update.is_enabled = is_enabled;
      if (typeof display_order === 'number') update.display_order = display_order;
      const { error } = await supabase.from('home_sections').update(update).eq('key', key);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'home-sections'] }),
  });
}

/* ---------------------------------------------------------------- about_content */
export function useAdminAboutContent() {
  return useQuery({
    queryKey: ['admin', 'about-content'],
    queryFn: async () => {
      const { data, error } = await supabase.from('about_content').select('*').order('key');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useUpdateAboutContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, data }: { key: string; data: Record<string, unknown> }) => {
      const { error } = await supabase.from('about_content').update({ data }).eq('key', key);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'about-content'] }),
  });
}

/* ---------------------------------------------------------------- footer_links */
export function useAdminFooterLinks() {
  return useQuery({
    queryKey: ['admin', 'footer-links'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('footer_links')
        .select('*')
        .order('column')
        .order('display_order');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useUpsertFooterLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: { id?: string; column: string; display_order: number; label: string; href: string; is_external: boolean }) => {
      if (row.id) {
        const { id, ...rest } = row;
        const { error } = await supabase.from('footer_links').update(rest).eq('id', id);
        if (error) throw error;
        return row;
      }
      const { error } = await supabase.from('footer_links').insert(row);
      if (error) throw error;
      return row;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'footer-links'] }),
  });
}

export function useDeleteFooterLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('footer_links').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'footer-links'] }),
  });
}

/* ---------------------------------------------------------------- projects */
export interface ProjectRow {
  id?: string;
  slug: string;
  title: string;
  summary: string | null;
  description_md: string | null;
  problem: string | null;
  approach: string | null;
  client: string | null;
  year: number | null;
  status: 'shipped' | 'ongoing' | 'concept';
  category: string;
  tech_stack: string[];
  hero_media_id: string | null;
  business_impact: Array<{ value: string; label: string; sublabel?: string }>;
  cta_label: string | null;
  cta_href: string | null;
  is_featured: boolean;
  display_order: number;
  status_publish: 'draft' | 'published' | 'archived';
  published_at: string | null;
}

export function useAdminProjects() {
  return useQuery({
    queryKey: ['admin', 'projects'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('display_order');
      if (error) throw error;
      return (data ?? []) as ProjectRow[];
    },
  });
}

export function useUpsertProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: ProjectRow) => {
      const payload = { ...row };
      if (row.id) {
        const { id, ...rest } = payload;
        const { data, error } = await supabase.from('projects').update(rest).eq('id', id).select().single();
        if (error) throw error;
        return data as ProjectRow;
      }
      delete (payload as Partial<ProjectRow>).id;
      const { data, error } = await supabase.from('projects').insert(payload).select().single();
      if (error) throw error;
      return data as ProjectRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'projects'] }),
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('projects').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'projects'] }),
  });
}

/* ---------------------------------------------------------------- project_categories */
export interface ProjectCategory {
  id?: string;
  name: string;
  slug: string;
  display_order: number;
}

export function useProjectCategories() {
  return useQuery({
    queryKey: ['admin', 'project-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_categories')
        .select('*')
        .order('display_order');
      if (error) throw error;
      return (data ?? []) as ProjectCategory[];
    },
  });
}

export function useUpsertProjectCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: Omit<ProjectCategory, 'id'> & { id?: string }) => {
      if (row.id) {
        const { id, ...rest } = row;
        const { data, error } = await supabase
          .from('project_categories')
          .update(rest)
          .eq('id', id)
          .select()
          .single();
        if (error) throw error;
        return data as ProjectCategory;
      }
      delete (row as Partial<ProjectCategory>).id;
      const { data, error } = await supabase
        .from('project_categories')
        .insert(row)
        .select()
        .single();
      if (error) throw error;
      return data as ProjectCategory;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'project-categories'] }),
  });
}

export function useDeleteProjectCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('project_categories').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'project-categories'] }),
  });
}

/* ---------------------------------------------------------------- posts */
export interface PostRow {
  id?: string;
  slug: string;
  title: string;
  excerpt: string | null;
  body_markdown: string;
  hero_media_id: string | null;
  author_id: string | null;
  category: string;
  tags: string[];
  reading_time_min: number | null;
  status: 'draft' | 'scheduled' | 'published' | 'archived';
  scheduled_at: string | null;
  published_at: string | null;
  meta_title: string | null;
  meta_description: string | null;
}

export function useAdminPosts() {
  return useQuery({
    queryKey: ['admin', 'posts'],
    queryFn: async () => {
      const { data, error } = await supabase.from('posts').select('*').order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as PostRow[];
    },
  });
}

export function useUpsertPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: PostRow) => {
      const payload = { ...row };
      // Compute reading time on save.
      const words = (row.body_markdown ?? '').trim().split(/\s+/).filter(Boolean).length;
      payload.reading_time_min = Math.max(1, Math.ceil(words / 200));
      if (row.status === 'published' && !row.published_at) {
        payload.published_at = new Date().toISOString();
      }
      if (row.id) {
        const { id, ...rest } = payload;
        const { data, error } = await supabase.from('posts').update(rest).eq('id', id).select().single();
        if (error) throw error;
        return data as PostRow;
      }
      delete (payload as Partial<PostRow>).id;
      const { data, error } = await supabase.from('posts').insert(payload).select().single();
      if (error) throw error;
      return data as PostRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'posts'] }),
  });
}

export function useDeletePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('posts').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'posts'] }),
  });
}

/* ---------------------------------------------------------------- team */
export type Department = 'leadership' | 'engineering' | 'design' | 'product' | 'operations';
export const DEPARTMENT_VALUES: Department[] = ['leadership', 'engineering', 'design', 'product', 'operations'];

export interface TeamRow {
  id?: string;
  slug: string;
  name: string;
  role: string;
  departments: Department[];
  bio: string | null;
  bio_md: string | null;
  skills: string[];
  portrait_media_id: string | null;
  linkedin_url: string | null;
  github_url: string | null;
  email: string | null;
  display_order: number;
  is_active: boolean;
  status: 'draft' | 'published' | 'archived';
}

export function useAdminTeam() {
  return useQuery({
    queryKey: ['admin', 'team'],
    queryFn: async () => {
      const { data, error } = await supabase.from('team_members').select('*').order('display_order');
      if (error) throw error;
      return (data ?? []) as TeamRow[];
    },
  });
}

export function useUpsertTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: TeamRow) => {
      if (row.id) {
        const { id, ...rest } = row;
        const { data, error } = await supabase.from('team_members').update(rest).eq('id', id).select().single();
        if (error) throw error;
        return data as TeamRow;
      }
      const { id: _omit, ...rest } = row;
      const { data, error } = await supabase.from('team_members').insert(rest).select().single();
      if (error) throw error;
      return data as TeamRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'team'] }),
  });
}

export function useDeleteTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('team_members').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'team'] }),
  });
}

/* ---------------------------------------------------------------- roles */
export interface RoleRow {
  id?: string;
  slug: string;
  title: string;
  department: string | null;
  location: string | null;
  type: 'full-time' | 'part-time' | 'contract' | 'intern' | null;
  description_md: string | null;
  responsibilities: string[];
  requirements: string[];
  nice_to_haves: string[];
  is_open: boolean;
  apply_email: string | null;
  status_publish: 'draft' | 'published' | 'archived';
  published_at: string | null;
}

export function useAdminRoles() {
  return useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: async () => {
      const { data, error } = await supabase.from('roles').select('*').order('published_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as RoleRow[];
    },
  });
}

export function useUpsertRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: RoleRow) => {
      const payload = { ...row };
      if (row.status_publish === 'published' && !row.published_at) {
        payload.published_at = new Date().toISOString();
      }
      if (row.id) {
        const { id, ...rest } = payload;
        const { data, error } = await supabase.from('roles').update(rest).eq('id', id).select().single();
        if (error) throw error;
        return data as RoleRow;
      }
      delete (payload as Partial<RoleRow>).id;
      const { data, error } = await supabase.from('roles').insert(payload).select().single();
      if (error) throw error;
      return data as RoleRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'roles'] }),
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('roles').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'roles'] }),
  });
}

/* ---------------------------------------------------------------- services */
export interface ServiceRow {
  id?: string;
  slug: string;
  title: string;
  summary: string | null;
  description_md: string | null;
  icon: string | null;
  deliverables: string[];
  tech_strip: string[];
  cta_label: string;
  cta_href: string;
  display_order: number;
  is_featured: boolean;
  meta_title: string | null;
  meta_description: string | null;
  status_publish: 'draft' | 'published' | 'archived';
  topic_id: string | null;
  /** Optional tiered-pricing payload — services without it use the standard layout. */
  pricing: unknown | null;
}

export function useAdminServices() {
  return useQuery({
    queryKey: ['admin', 'services'],
    queryFn: async () => {
      const { data, error } = await supabase.from('services').select('*').order('display_order');
      if (error) throw error;
      return (data ?? []) as ServiceRow[];
    },
  });
}

export function useUpsertService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: ServiceRow) => {
      if (row.id) {
        const { id, ...rest } = row;
        const { data, error } = await supabase.from('services').update(rest).eq('id', id).select().single();
        if (error) throw error;
        return data as ServiceRow;
      }
      const { id: _omit, ...rest } = row;
      const { data, error } = await supabase.from('services').insert(rest).select().single();
      if (error) throw error;
      return data as ServiceRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'services'] }),
  });
}

export function useDeleteService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('services').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'services'] }),
  });
}

/* ---------------------------------------------------------------- topics */
export interface TopicRow {
  id?: string;
  name: string;
  slug: string;
  display_order: number;
  is_active: boolean;
}

export function useAdminTopics() {
  return useQuery({
    queryKey: ['admin', 'topics'],
    queryFn: async () => {
      const { data, error } = await supabase.from('topics').select('*').order('display_order');
      if (error) throw error;
      return (data ?? []) as TopicRow[];
    },
  });
}

export function useUpsertTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: TopicRow) => {
      if (row.id) {
        const { id, ...rest } = row;
        const { data, error } = await supabase.from('topics').update(rest).eq('id', id).select().single();
        if (error) throw error;
        return data as TopicRow;
      }
      const { id: _omit, ...rest } = row;
      const { data, error } = await supabase.from('topics').insert(rest).select().single();
      if (error) throw error;
      return data as TopicRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'topics'] }),
  });
}

export function useDeleteTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('topics').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'topics'] }),
  });
}

/* ---------------------------------------------------------------- industries */
export function useAdminIndustries() {
  return useQuery({
    queryKey: ['admin', 'industries'],
    queryFn: async () => {
      const { data, error } = await supabase.from('industries').select('*').order('display_order');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useUpsertIndustry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: { id?: string; slug: string; name: string; icon: string | null; outcome: string | null; display_order: number; is_enabled: boolean }) => {
      if (row.id) {
        const { id, ...rest } = row;
        const { error } = await supabase.from('industries').update(rest).eq('id', id);
        if (error) throw error;
        return row;
      }
      const { error } = await supabase.from('industries').insert(row);
      if (error) throw error;
      return row;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'industries'] }),
  });
}

export function useDeleteIndustry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('industries').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'industries'] }),
  });
}

/* ---------------------------------------------------------------- FAQ */
export interface FaqRow {
  id?: string;
  question: string;
  answer_md: string;
  category: 'general' | 'projects' | 'payments' | 'ip' | 'nda' | 'hiring';
  display_order: number;
  is_published: boolean;
}

export function useAdminFaq() {
  return useQuery({
    queryKey: ['admin', 'faq'],
    queryFn: async () => {
      const { data, error } = await supabase.from('faq').select('*').order('display_order');
      if (error) throw error;
      return (data ?? []) as FaqRow[];
    },
  });
}

export function useUpsertFaq() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: FaqRow) => {
      if (row.id) {
        const { id, ...rest } = row;
        const { data, error } = await supabase.from('faq').update(rest).eq('id', id).select().single();
        if (error) throw error;
        return data as FaqRow;
      }
      const { id: _omit, ...rest } = row;
      const { data, error } = await supabase.from('faq').insert(rest).select().single();
      if (error) throw error;
      return data as FaqRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'faq'] }),
  });
}

export function useDeleteFaq() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('faq').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'faq'] }),
  });
}

/* ---------------------------------------------------------------- legal pages */
export interface LegalRow {
  id?: string;
  slug: 'privacy' | 'terms' | 'cookies';
  title: string;
  body_md: string;
  effective_at: string | null;
}

export function useAdminLegalPages() {
  return useQuery({
    queryKey: ['admin', 'legal'],
    queryFn: async () => {
      const { data, error } = await supabase.from('legal_pages').select('*').order('slug');
      if (error) throw error;
      return (data ?? []) as LegalRow[];
    },
  });
}

export function useUpdateLegalPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: LegalRow) => {
      if (!row.id) throw new Error('Missing id');
      const { id, ...rest } = row;
      const { data, error } = await supabase.from('legal_pages').update(rest).eq('id', id).select().single();
      if (error) throw error;
      return data as LegalRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'legal'] }),
  });
}

/* ---------------------------------------------------------------- inbox */
export interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  company: string | null;
  topic: string;
  budget: string | null;
  message: string;
  status: 'new' | 'replied' | 'resolved' | 'spam';
  assigned_to: string | null;
  created_at: string;
}

export function useAdminContactSubmissions(status?: string) {
  return useQuery({
    queryKey: ['admin', 'contact-submissions', status ?? 'all'],
    queryFn: async () => {
      let q = supabase
        .from('contact_submissions')
        .select('id, name, email, company, topic, budget, message, status, assigned_to, created_at')
        .order('created_at', { ascending: false });
      if (status && status !== 'all') q = q.eq('status', status);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as ContactSubmission[];
    },
  });
}

export function useUpdateContactStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ContactSubmission['status'] }) => {
      const { error } = await supabase.from('contact_submissions').update({ status }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'contact-submissions'] }),
  });
}

export interface ApplicationSubmission {
  id: string;
  role_id: string | null;
  role_slug: string | null;
  full_name: string;
  email: string;
  phone: string | null;
  linkedin_url: string | null;
  portfolio_url: string | null;
  cover_letter: string | null;
  cv_path: string | null;
  consent_at: string | null;
  status: 'new' | 'reviewing' | 'interview' | 'offer' | 'rejected' | 'hired';
  created_at: string;
}

export function useInboxApplications(status?: string) {
  return useQuery({
    queryKey: ['admin', 'inbox-applications', status ?? 'all'],
    queryFn: async () => {
      let q = supabase
        .from('applications')
        .select('id, role_id, role_slug, full_name, email, phone, linkedin_url, portfolio_url, cover_letter, cv_path, consent_at, status, created_at')
        .order('created_at', { ascending: false });
      if (status && status !== 'all') q = q.eq('status', status);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as ApplicationSubmission[];
    },
  });
}

export function useUpdateInboxApplicationStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ApplicationSubmission['status'] }) => {
      const { error } = await supabase.from('applications').update({ status }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'inbox-applications'] }),
  });
}

export async function getApplicationCvUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from('cvs').createSignedUrl(path, 300);
  if (error) throw error;
  return data.signedUrl;
}

/* ---------------------------------------------------------------- influencers */
export interface InfluencerRow {
  id?: string;
  slug: string;
  name: string;
  headline: string | null;
  bio_md: string | null;
  portrait_media_id: string | null;
  tiktok_url: string | null;
  instagram_url: string | null;
  youtube_url: string | null;
  /** Facebook page URL. Renamed from x_url in 0017 — column reflects the new
   *  social slot the influencer wants to promote. */
  facebook_url: string | null;
  joined_at: string | null;
  is_active: boolean;
  status: 'draft' | 'published' | 'archived';
  published_at: string | null;
}

export const EMPTY_INFLUENCER: InfluencerRow = {
  slug: '',
  name: '',
  headline: null,
  bio_md: null,
  portrait_media_id: null,
  tiktok_url: null,
  instagram_url: null,
  youtube_url: null,
  facebook_url: null,
  joined_at: null,
  is_active: true,
  status: 'draft',
  published_at: null,
};

export function useAdminInfluencers() {
  return useQuery({
    queryKey: ['admin', 'influencers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('influencers')
        .select('*')
        .order('joined_at', { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as InfluencerRow[];
    },
  });
}

export function useUpsertInfluencer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: InfluencerRow) => {
      const payload = { ...row };
      if (payload.status === 'published' && !payload.published_at) {
        payload.published_at = new Date().toISOString();
      }
      const { data, error } = await supabase
        .from('influencers')
        .upsert(payload)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'influencers'] }),
  });
}

export function useDeleteInfluencer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('influencers').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'influencers'] }),
  });
}

/* ---------------------------------------------------------------- contents */
export interface ContentRow {
  id?: string;
  slug: string;
  title: string;
  summary: string;
  description_md: string | null;
  cover_media_id: string | null;
  primary_influencer_id: string | null;
  tiktok_url: string | null;
  instagram_url: string | null;
  youtube_url: string | null;
  x_url: string | null;
  category: 'short-drama' | 'micro-series' | 'promo' | 'behind-the-scenes';
  display_order: number;
  is_featured: boolean;
  status: 'draft' | 'published' | 'archived';
  published_at: string | null;
}

export const EMPTY_CONTENT: ContentRow = {
  slug: '',
  title: '',
  summary: '',
  description_md: null,
  cover_media_id: null,
  primary_influencer_id: null,
  tiktok_url: null,
  instagram_url: null,
  youtube_url: null,
  x_url: null,
  category: 'short-drama',
  display_order: 0,
  is_featured: false,
  status: 'draft',
  published_at: null,
};

export function useAdminContents() {
  return useQuery({
    queryKey: ['admin', 'contents'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contents')
        .select('*')
        .order('display_order', { ascending: true });
      if (error) throw error;
      return (data ?? []) as ContentRow[];
    },
  });
}

export function useUpsertContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: ContentRow) => {
      const payload = { ...row };
      if (payload.status === 'published' && !payload.published_at) {
        payload.published_at = new Date().toISOString();
      }
      const { data, error } = await supabase
        .from('contents')
        .upsert(payload)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'contents'] });
    },
  });
}

export function useDeleteContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('contents').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'contents'] }),
  });
}

/* ---------------------------------------------------------------- media */
export function useAdminMedia() {
  return useQuery({
    queryKey: ['admin', 'media'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('media_assets')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useUpdateMediaAlt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, alt, tags }: { id: string; alt: string; tags?: string[] }) => {
      const update: Record<string, unknown> = { alt };
      if (tags) update.tags = tags;
      const { error } = await supabase.from('media_assets').update(update).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'media'] }),
  });
}

export function useDeleteMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, bucket, path }: { id: string; bucket: string; path: string }) => {
      const { error: storageErr } = await supabase.storage.from(bucket).remove([path]);
      if (storageErr) throw storageErr;
      const { error } = await supabase.from('media_assets').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'media'] }),
  });
}

export function publicAssetUrl(bucket: string, path: string): string {
  const base = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '';
  return `${base}/storage/v1/object/public/${bucket}/${path}`;
}

/* ---------------------------------------------------------------- applications */
export type ApplicationStatus = 'new' | 'reviewing' | 'interview' | 'offer' | 'rejected' | 'withdrawn';

export const APPLICATION_STATUSES: ApplicationStatus[] = [
  'new',
  'reviewing',
  'interview',
  'offer',
  'rejected',
  'withdrawn',
];

export interface ApplicationRow {
  id: string;
  role_id: string | null;
  role_slug: string | null;
  full_name: string;
  email: string;
  phone: string | null;
  linkedin_url: string | null;
  portfolio_url: string | null;
  cover_letter: string | null;
  cv_path: string | null;
  consent_at: string;
  status: ApplicationStatus;
  assigned_to: string | null;
  created_at: string;
}

export function useAdminApplications(roleSlug?: string | null) {
  return useQuery<ApplicationRow[]>({
    queryKey: ['admin', 'applications', roleSlug ?? 'all'],
    queryFn: async () => {
      let q = supabase
        .from('applications')
        .select(
          'id, role_id, role_slug, full_name, email, phone, linkedin_url, portfolio_url, cover_letter, cv_path, consent_at, status, assigned_to, created_at',
        )
        .order('created_at', { ascending: false });
      if (roleSlug && roleSlug !== 'all') q = q.eq('role_slug', roleSlug);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as ApplicationRow[];
    },
  });
}

export function useUpdateApplicationStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ApplicationStatus }) => {
      const { error } = await supabase.from('applications').update({ status }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'applications'] }),
  });
}

/**
 * Delete an application row and its attached CV. Calls the
 * `delete-application` Edge Function which (a) verifies the caller is
 * owner/admin and (b) cleans the cvs storage bucket via service-role.
 *
 * Invalidate both the dedicated applications query key and the legacy
 * inbox key so neither view shows a ghost row.
 */
export function useDeleteApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, cv_path }: { id: string; cv_path: string | null }) => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        error?: string;
        cv_removed?: boolean;
      }>('delete-application', { body: { id, cv_path } });
      if (error) throw new Error(error.message);
      if (data && data.ok === false) throw new Error(data.error ?? 'Delete failed.');
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'applications'] });
      qc.invalidateQueries({ queryKey: ['admin', 'inbox-applications'] });
    },
  });
}

/**
 * Produce a short-lived signed URL for a private CV blob in the `cvs` bucket.
 * Returns null if no path was supplied or the bucket rejects the request.
 */
export async function signedCvUrl(path: string | null | undefined): Promise<string | null> {
  if (!path) return null;
  const { data, error } = await supabase.storage.from('cvs').createSignedUrl(path, 60 * 10);
  if (error) return null;
  return data.signedUrl;
}